import type { AxiosHeaders } from 'axios';
import { api } from './index';
import { type VitalSignRecord } from './vital-sign-records';
import type { ClinicalAuthor } from '../utils/clinical-author';
import type { ClinicalVoidable } from '../utils/clinical-void';

const BASE_PATH = '/api/internal/clinical_records';

export interface ClinicalReportImage extends ClinicalVoidable {
  id: number;
  imageUrlLarge: string;
  humanCreatedAt: string;
  formattedText: string;
  author: ClinicalAuthor | null;
}

export interface ClinicalReportText extends ClinicalVoidable {
  id: number;
  text: string;
  humanCreatedAt: string;
  formattedText: string;
  edited: boolean;
  author: ClinicalAuthor | null;
}

export interface ClinicalMedicalRecord extends ClinicalVoidable {
  id: number;
  reason: string;
  diagnosis: string;
  treatment: string;
  personalHistory: string;
  currentIllness: string;
  physicalExam: string;
  humanCreatedAt: string;
  humanUpdatedAt: string;
  caregiverFullName: string;
  edited: boolean;
  author: ClinicalAuthor | null;
}

export interface ClinicalIncidentRecord extends ClinicalVoidable {
  id: number;
  observations: string;
  humanReportedAt: string;
  incidentDefinitionName: string;
  incidentDefinitionDescription: string;
  author: ClinicalAuthor | null;
}

export type ClinicalTaskRecordState = 'pending' | 'completed' | 'incomplete';

export interface ClinicalTaskRecord extends ClinicalVoidable {
  id: number;
  humanScheduledAt: string;
  humanRecordedAt: string | null;
  aasmState: ClinicalTaskRecordState;
  observations: string | null;
  taskName: string;
  taskCategoryName: string | null;
  timeType: 'am' | 'pm' | 'specific';
  edited: boolean;
  author: ClinicalAuthor | null;
}

export interface ClinicalShift {
  id: number;
  humanDate: string;
  humanTime: string;
  startDatetime: string;
  caregiverFullName: string;
  caregiverCareer: string;
  caregiverHumanCareer: string;
  reportImagesSummary: string;
  reportImagesCount: number;
  reportTextsCount: number;
  vitalSignRecordsCount: number;
  incidentRecordsCount: number;
  taskRecordsCount: number;
  pendingTaskRecordsCount: number;
  completedTaskRecordsCount: number;
  incompleteTaskRecordsCount: number;
  hasMedicalRecord: boolean;
  reportImages: ClinicalReportImage[];
  reportTexts: ClinicalReportText[];
  // Anulados included, oldest first.
  medicalRecords: ClinicalMedicalRecord[];
  vitalSignRecords: VitalSignRecord[];
  incidentRecords: ClinicalIncidentRecord[];
  taskRecords: ClinicalTaskRecord[];
}

export interface ClinicalRecordsPdf {
  headers: AxiosHeaders;
  data: Blob;
}

export const clinicalRecordsApi = {
  index(patientId: number, startDate?: string, endDate?: string): Promise<ClinicalShift[]> {
    let url = `${BASE_PATH}?patient_id=${encodeURIComponent(patientId)}`;
    if (startDate) url += `&start_date=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&end_date=${encodeURIComponent(endDate)}`;

    return api({
      method: 'get',
      url,
    }).then(response => response.data.shifts);
  },

  indexAsPdf(
    patientId: number,
    startDate?: string,
    endDate?: string,
    includeImages = false,
  ): Promise<ClinicalRecordsPdf> {
    let url = `${BASE_PATH}.pdf?patient_id=${encodeURIComponent(patientId)}` +
      `&include_images=${encodeURIComponent(includeImages)}`;
    if (startDate) url += `&start_date=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&end_date=${encodeURIComponent(endDate)}`;

    return api({
      method: 'get',
      url,
      responseType: 'blob',
    });
  },
};
